import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ImageOff, Pencil, Trash2 } from "lucide-react";
import StatusBadge from "./StatusBadge";

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Map schema `heroAspect` hints → Tailwind aspect-ratio classes.
// "video" (16:9) is the default because most content types are
// landscape hero images (blogs, insights, news covers). People-oriented
// collections (leaders, brokers) set "square" so faces don't get
// center-cropped out of the frame.
const ASPECT_CLASS = {
  video: "aspect-[16/9]",
  square: "aspect-square",
  portrait: "aspect-[4/5]",
};

export default function ContentCard({
  doc,
  heroField,
  // Aspect for the card-mode hero (schema-driven). Defaults to 16:9.
  heroAspect = "video",
  // CSS object-position override — either a static string from the
  // schema OR pulled off the doc (`photoObjectPosition`) so per-photo
  // face-crop nudges made in the CMS form take effect in the thumbnail.
  heroObjectPosition,
  dateField = "date",
  editTo,
  published,
  onDelete,
  meta,
  // "list" (compact row, no image fetched) or "card" (thumbnail-first
  // grid tile). Undefined falls back to "list" for collections without
  // a hero field, "card" for those with one — preserves the previous
  // behavior for callers that don't pass this prop yet.
  viewMode,
}) {
  const dateValue = doc?.[dateField];
  const hasHeroField = Boolean(heroField);
  const effectiveMode =
    viewMode || (hasHeroField ? "card" : "list");
  // List mode now shows a small avatar when a hero field exists, so
  // fetch the URL in both modes. The image is only ~40px so bandwidth
  // is a rounding error compared to the tile size in card mode.
  const heroUrl = hasHeroField ? doc?.[heroField] : null;
  const objectPosition =
    doc?.photoObjectPosition || heroObjectPosition || "center";
  const heroAspectClass = ASPECT_CLASS[heroAspect] || ASPECT_CLASS.video;

  if (effectiveMode === "list") {
    // Title comes FIRST so every row's title sits at the same left
    // edge. Status pill flows to the right of the title on the same
    // line and wraps if the row is narrow. Meta (date · dept · type)
    // and description sit below the title. Consistent with the
    // ListingsManager / BuildingsManager list layouts.
    return (
      <motion.div
        whileHover={{ y: -1 }}
        transition={{ duration: 0.15 }}
        className="group bg-white rounded-xl ring-1 ring-gray-100 shadow-sm hover:shadow-md transition-shadow flex items-center gap-4 pl-3 pr-2 py-3"
      >
        {/* Compact avatar so people-oriented collections (brokers,
            leaders) are recognizable at a glance without switching to
            card view. Same crop rules as the card-mode thumbnail. */}
        {heroUrl ? (
          <Link
            to={editTo}
            className="shrink-0 h-11 w-11 rounded-lg overflow-hidden bg-gray-100 block"
          >
            <img
              src={heroUrl}
              alt=""
              className="w-full h-full object-cover"
              style={{ objectPosition }}
            />
          </Link>
        ) : null}
        <Link to={editTo} className="flex-1 min-w-0 block">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-leechiuBlue truncate m-0">
              {doc.title || "Untitled"}
            </h3>
            <StatusBadge published={published} />
          </div>
          {(dateValue || meta) ? (
            <div className="flex items-center gap-2 text-[11px] text-gray-500 uppercase tracking-wide mt-1">
              {dateValue ? <span>{formatDate(dateValue)}</span> : null}
              {dateValue && meta ? <span aria-hidden="true">·</span> : null}
              {meta ? <span className="truncate">{meta}</span> : null}
            </div>
          ) : null}
          {doc.description ? (
            <p className="text-xs text-gray-500 line-clamp-1 mt-1">
              {doc.description}
            </p>
          ) : null}
        </Link>
        <div className="flex items-center gap-1 shrink-0">
          <Link
            to={editTo}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-gray-600 hover:bg-gray-50"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </Link>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onDelete?.(doc);
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.15 }}
      className="group bg-white rounded-2xl overflow-hidden ring-1 ring-gray-100 shadow-sm hover:shadow-md transition-shadow"
    >
      <Link to={editTo} className="block">
        <div
          className={`relative ${heroAspectClass} bg-gray-100 overflow-hidden`}
        >
          {heroUrl ? (
            <img
              src={heroUrl}
              alt={doc.title || ""}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
              style={{ objectPosition }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <ImageOff className="w-10 h-10" />
            </div>
          )}
          <div className="absolute top-3 left-3">
            <StatusBadge published={published} />
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between text-[11px] text-gray-500 uppercase tracking-wide mb-2">
            <span>{formatDate(dateValue)}</span>
            {meta ? <span className="truncate ml-2">{meta}</span> : null}
          </div>
          <h3 className="text-base font-semibold text-leechiuBlue line-clamp-2 mb-1">
            {doc.title || "Untitled"}
          </h3>
          {doc.description ? (
            <p className="text-sm text-gray-500 line-clamp-2">
              {doc.description}
            </p>
          ) : null}
        </div>
      </Link>
      <div className="px-4 pb-4 pt-1 flex items-center justify-end gap-1">
        <Link
          to={editTo}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-gray-600 hover:bg-gray-50"
        >
          <Pencil className="w-3.5 h-3.5" />
          Edit
        </Link>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onDelete?.(doc);
          }}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-red-600 hover:bg-red-50"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete
        </button>
      </div>
    </motion.div>
  );
}
